import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import {
  Fuel, Plus, Search, RefreshCw, Truck, Calendar, DollarSign,
  TrendingUp, TrendingDown, BarChart3, Filter, Eye, Edit, Trash2,
  MapPin, Clock, ChevronDown, Download, FileText } from
'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { useFleetAPI, useVehicles } from '../hooks/useFleetData';
import { getVehicleIllustration } from '../components/VehicleIllustrations';


const fuelTypeColors = {
  DIESEL: 'bg-amber-100 text-amber-700',
  GASOLINE: 'bg-green-100 text-green-700',
  ETHANOL: 'bg-blue-100 text-blue-700',
  ELECTRIC: 'bg-purple-100 text-purple-700',
  FLEX: 'bg-teal-100 text-teal-700',
  CNG: 'bg-gray-100 text-gray-700',
  LPG: 'bg-orange-100 text-orange-700'
};

const FuelLogsPage = () => {const { t } = useTranslation("fleet");
  const [activeTab, setActiveTab] = useState('logs');
  const [fuelLogs, setFuelLogs] = useState([]);
  const [consumptionData, setConsumptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [dateRange, setDateRange] = useState('30'); // days
  const { vehicles } = useVehicles({});
  const { fetchAPI } = useFleetAPI();

  useEffect(() => {
    loadData();
  }, [dateRange]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [logs, consumption] = await Promise.all([
      fetchAPI('/fuel-logs'),
      fetchAPI('/consumption/summary')]
      );
      setFuelLogs(logs);
      setConsumptionData(consumption);
    } catch (err) {
      console.error('Failed to load fuel data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate summary stats
  const totalLiters = fuelLogs.reduce((sum, f) => sum + (f.quantity_liters || 0), 0);
  const totalCost = fuelLogs.reduce((sum, f) => sum + (f.total_cost || 0), 0);
  const avgCostPerLiter = totalLiters > 0 ? totalCost / totalLiters : 0;
  const transactionsCount = fuelLogs.length;

  const filteredLogs = fuelLogs.filter((log) =>
  log.vehicle_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
  log.station_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
  log.driver_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="fuel-logs-title">{t("fuelManagement", "Fuel Management")}

          </h1>
          <p className="text-gray-500">{t("trackFuelConsumption", "Track fuel consumption, costs, and efficiency")}</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            className="border rounded-lg px-3 py-2 text-sm bg-white"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}>
            
            <option value="7">{t("last7Days", "Last 7 days")}</option>
            <option value="30">{t("last30Days", "Last 30 days")}</option>
            <option value="90">{t("last90Days", "Last 90 days")}</option>
            <option value="365">{t("lastYear", "Last year")}</option>
          </select>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search logs..."
              className="pl-9 w-64 bg-white"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)} />
            
          </div>
          <Button variant="outline" size="sm" onClick={loadData}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button
            className="bg-red-500 hover:bg-red-600 text-white"
            size="sm"
            onClick={() => setShowAddModal(true)}>
            
            <Plus className="w-4 h-4 mr-2" />{t("addFuelLog", "Add Fuel Log")}

          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white border-0 shadow-lg">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-80">{t("totalFuel", "Total Fuel")}</p>
                <p className="text-2xl font-bold">{totalLiters.toLocaleString()} L</p>
                <p className="text-xs opacity-70 mt-1">{transactionsCount}{t("transactions", "transactions")}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Fuel className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0 shadow-lg">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-80">{t("totalCost", "Total Cost")}</p>
                <p className="text-2xl font-bold">{t("r", "R$")}{totalCost.toLocaleString()}</p>
                <p className="text-xs opacity-70 mt-1">{t("last", "Last")}{dateRange}{t("days", "days")}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <DollarSign className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-0 shadow-lg">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-80">{t("avgCostliter", "Avg Cost/Liter")}</p>
                <p className="text-2xl font-bold">{t("r", "R$")}{avgCostPerLiter.toFixed(2)}</p>
                <p className="text-xs opacity-70 mt-1">
                  {avgCostPerLiter > 6 ?
                  <span className="flex items-center">
                      <TrendingUp className="w-3 h-3 mr-1" />{t("aboveAvg", "Above avg")}
                  </span> :

                  <span className="flex items-center">
                      <TrendingDown className="w-3 h-3 mr-1" />{t("goodPrice", "Good price")}
                  </span>
                  }
                </p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <BarChart3 className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-violet-600 text-white border-0 shadow-lg">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-80">{t("vehiclesFueled", "Vehicles Fueled")}</p>
                <p className="text-2xl font-bold">
                  {new Set(fuelLogs.map((f) => f.vehicle_id)).size}
                </p>
                <p className="text-xs opacity-70 mt-1">{t("activeFleet", "Active fleet")}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Truck className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-white p-1 rounded-lg shadow-sm">
          <TabsTrigger value="logs" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
            <FileText className="w-4 h-4 mr-2" />{t("fuelLogs", "Fuel Logs")}

          </TabsTrigger>
          <TabsTrigger value="by-vehicle" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
            <Truck className="w-4 h-4 mr-2" />{t("byVehicle", "By Vehicle")}

          </TabsTrigger>
          <TabsTrigger value="analytics" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
            <BarChart3 className="w-4 h-4 mr-2" />{t("analytics", "Analytics")}

          </TabsTrigger>
        </TabsList>

        {/* Fuel Logs Tab */}
        <TabsContent value="logs">
          <Card className="bg-white border-0 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Fuel className="w-5 h-5 text-amber-500" />{t("fuelTransactions", "Fuel Transactions")}

                </CardTitle>
                <Button variant="outline" size="sm">
                  <Download className="w-4 h-4 mr-2" />{t("export", "Export")}

                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ?
              <div className="flex items-center justify-center h-32">
                  <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
                </div> :
              filteredLogs.length === 0 ?
              <div className="text-center py-12 text-gray-500">
                  <Fuel className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <h3 className="text-lg font-medium text-gray-900">{t("noFuelLogs", "No Fuel Logs")}</h3>
                  <p>{t("startRecordingFuel", "Start recording fuel transactions")}</p>
                  <Button
                  className="mt-4 bg-red-500 hover:bg-red-600 text-white"
                  onClick={() => setShowAddModal(true)}>
                  
                    <Plus className="w-4 h-4 mr-2" />{t("addFuelLog", "Add Fuel Log")}

                </Button>
                </div> :

              <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-medium text-gray-500">{t("date", "Date")}</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-500">{t("vehicle", "Vehicle")}</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-500">{t("driver", "Driver")}</th>
                        <th className="text-center py-3 px-4 font-medium text-gray-500">{t("fuelType", "Fuel Type")}</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-500">{t("liters", "Liters")}</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-500">{t("pricel", "Price/L")}</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-500">{t("total", "Total")}</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-500">{t("station", "Station")}</th>
                        <th className="text-center py-3 px-4 font-medium text-gray-500">{t("actions", "Actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLogs.map((log) =>
                    <tr key={log.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              <div>
                                <p className="font-medium text-gray-900">
                                  {log.refuel_date ? new Date(log.refuel_date).toLocaleDateString() : '-'}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {log.refuel_date ? new Date(log.refuel_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <Truck className="w-4 h-4 text-gray-400" />
                              <span className="font-medium">{log.vehicle_code || `#${log.vehicle_id}`}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-gray-600">
                            {log.driver_name || '-'}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Badge className={fuelTypeColors[log.fuel_type] || 'bg-gray-100 text-gray-700'}>
                              {log.fuel_type || 'N/A'}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-right font-medium">
                            {(log.quantity_liters || 0).toLocaleString()} L
                          </td>
                          <td className="py-3 px-4 text-right text-gray-600">{t("r", "R$")}
                        {(log.unit_price || 0).toFixed(2)}
                          </td>
                          <td className="py-3 px-4 text-right font-bold text-green-600">{t("r", "R$")}
                        {(log.total_cost || 0).toLocaleString()}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-600">{log.station_name || '-'}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <Button variant="ghost" size="sm">
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm">
                                <Edit className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                    )}
                    </tbody>
                  </table>
                </div>
              }
            </CardContent>
          </Card>
        </TabsContent>

        {/* By Vehicle Tab */}
        <TabsContent value="by-vehicle">
          <Card className="bg-white border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-blue-500" />{t("fuelConsumptionBy", "Fuel Consumption by Vehicle")}

              </CardTitle>
            </CardHeader>
            <CardContent>
              {consumptionData?.summary?.length > 0 ?
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {consumptionData.summary.map((item, idx) => {
                  const vehicle = vehicles.find((v) => v.id === item.vehicle_id);
                  const VehicleIcon = vehicle ? getVehicleIllustration(vehicle.vehicle_type) : Truck;

                  return (
                    <div key={idx} className="border rounded-xl p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h3 className="font-bold text-gray-900">
                              {vehicle?.vehicle_code || `Vehicle #${item.vehicle_id}`}
                            </h3>
                            <p className="text-xs text-gray-500">
                              {vehicle?.make} {vehicle?.model}
                            </p>
                          </div>
                          <Badge className="bg-amber-100 text-amber-700">
                            {item.fuel_transactions}{t("fills", "fills")}
                        </Badge>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-3 mb-3">
                          {typeof VehicleIcon === 'function' ?
                        <VehicleIcon className="w-full h-16" fillColor="#ffffff" /> :

                        <Truck className="w-full h-16 text-gray-400" />
                        }
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-center">
                          <div className="bg-amber-50 rounded-lg p-2">
                            <p className="text-xs text-gray-500">{t("totalFuel", "Total Fuel")}</p>
                            <p className="font-bold text-amber-600">{item.fuel_liters?.toLocaleString() || 0} L</p>
                          </div>
                          <div className="bg-green-50 rounded-lg p-2">
                            <p className="text-xs text-gray-500">{t("totalCost", "Total Cost")}</p>
                            <p className="font-bold text-green-600">{t("r", "R$")}{item.fuel_cost?.toLocaleString() || 0}</p>
                          </div>
                        </div>
                      </div>);

                })}
                </div> :

              <div className="text-center py-12 text-gray-500">
                  <Truck className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <h3 className="text-lg font-medium text-gray-900">{t("noConsumptionData", "No Consumption Data")}</h3>
                  <p>{t("addFuelLogs", "Add fuel logs to see consumption by vehicle")}</p>
                </div>
              }
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-white border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-purple-500" />{t("costTrend", "Cost Trend")}

                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64 flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>{t("chartVisualizationComing", "Chart visualization coming soon")}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-500" />{t("efficiencyMetrics", "Efficiency Metrics")}

                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">{t("averageKml", "Average km/L")}</span>
                    <span className="font-bold text-gray-900">
                      {consumptionData?.summary?.[0]?.cost_per_km?.toFixed(2) || '3.5'}{t("kml", "km/L")}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">{t("mostEfficientVehicle", "Most efficient vehicle")}</span>
                    <span className="font-bold text-green-600">
                      {consumptionData?.summary?.[0]?.vehicle_code || 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">{t("avgFillupCost", "Avg fill-up cost")}</span>
                    <span className="font-bold text-gray-900">{t("r", "R$")}
                      {transactionsCount > 0 ? (totalCost / transactionsCount).toFixed(2) : '0'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">{t("monthlyProjection", "Monthly projection")}</span>
                    <span className="font-bold text-amber-600">{t("r", "R$")}
                      {(totalCost / parseInt(dateRange) * 30).toLocaleString()}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Fuel Log Modal */}
      {showAddModal &&
      <AddFuelLogModal
        vehicles={vehicles}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          setShowAddModal(false);
          loadData();
        }} />

      }
    </div>);

};

// Add Fuel Log Modal
const AddFuelLogModal = ({ vehicles, onClose, onSuccess }) => {const { t } = useTranslation("fleet");
  const [formData, setFormData] = useState({
    vehicle_id: '',
    fuel_type: 'DIESEL',
    quantity_liters: '',
    unit_price: '',
    total_cost: '',
    station_name: '',
    odometer_at_fill: '',
    is_full_tank: true,
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const { fetchAPI } = useFleetAPI();

  // Auto-calculate total cost
  useEffect(() => {
    if (formData.quantity_liters && formData.unit_price) {
      const total = parseFloat(formData.quantity_liters) * parseFloat(formData.unit_price);
      setFormData((f) => ({ ...f, total_cost: total.toFixed(2) }));
    }
  }, [formData.quantity_liters, formData.unit_price]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await fetchAPI('/fuel-logs', {
        method: 'POST',
        body: JSON.stringify({
          ...formData,
          vehicle_id: parseInt(formData.vehicle_id),
          quantity_liters: parseFloat(formData.quantity_liters),
          unit_price: parseFloat(formData.unit_price),
          total_cost: parseFloat(formData.total_cost),
          odometer_at_fill: formData.odometer_at_fill ? parseInt(formData.odometer_at_fill) : null
        })
      });
      onSuccess();
    } catch (err) {
      console.error('Failed to create fuel log:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <Card className="w-full max-w-lg m-4 bg-white max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <CardHeader className="border-b sticky top-0 bg-white">
          <CardTitle className="flex items-center gap-2">
            <Fuel className="w-5 h-5 text-amber-500" />{t("addFuelLog", "Add Fuel Log")}

          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">{t("vehicle", "Vehicle *")}</label>
              <select
                className="w-full border rounded-lg p-2.5"
                value={formData.vehicle_id}
                onChange={(e) => setFormData((f) => ({ ...f, vehicle_id: e.target.value }))}
                required>
                
                <option value="">{t("selectVehicle", "Select vehicle...")}</option>
                {vehicles.map((v) =>
                <option key={v.id} value={v.id}>
                    {v.vehicle_code} - {v.make} {v.model} ({v.license_plate})
                  </option>
                )}
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">{t("fuelType", "Fuel Type *")}</label>
              <select
                className="w-full border rounded-lg p-2.5"
                value={formData.fuel_type}
                onChange={(e) => setFormData((f) => ({ ...f, fuel_type: e.target.value }))}
                required>
                
                <option value="DIESEL">{t("diesel", "Diesel")}</option>
                <option value="GASOLINE">{t("gasoline", "Gasoline")}</option>
                <option value="ETHANOL">{t("ethanol", "Ethanol")}</option>
                <option value="FLEX">{t("flex", "Flex")}</option>
                <option value="ELECTRIC">{t("electric", "Electric")}</option>
                <option value="CNG">{t("cng", "CNG")}</option>
                <option value="LPG">{t("lpg", "LPG")}</option>
              </select>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("liters", "Liters *")}</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.quantity_liters}
                  onChange={(e) => setFormData((f) => ({ ...f, quantity_liters: e.target.value }))}
                  placeholder="350"
                  required />
                
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("pricelR", "Price/L (R$) *")}</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.unit_price}
                  onChange={(e) => setFormData((f) => ({ ...f, unit_price: e.target.value }))}
                  placeholder="5.89"
                  required />
                
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("totalR", "Total (R$)")}</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.total_cost}
                  onChange={(e) => setFormData((f) => ({ ...f, total_cost: e.target.value }))}
                  placeholder="2061.50"
                  className="bg-gray-50" />
                
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">{t("stationName", "Station Name")}</label>
              <Input
                value={formData.station_name}
                onChange={(e) => setFormData((f) => ({ ...f, station_name: e.target.value }))}
                placeholder="e.g., Posto Ipiranga BR-101" />
              
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">{t("odometerKm", "Odometer (km)")}</label>
              <Input
                type="number"
                value={formData.odometer_at_fill}
                onChange={(e) => setFormData((f) => ({ ...f, odometer_at_fill: e.target.value }))}
                placeholder="125000" />
              
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_full_tank}
                onChange={(e) => setFormData((f) => ({ ...f, is_full_tank: e.target.checked }))}
                className="w-4 h-4 rounded border-gray-300 text-red-500" />
              
              <label className="text-sm text-gray-700">{t("fullTankFillup", "Full tank fill-up")}</label>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">{t("notes", "Notes")}</label>
              <textarea
                className="w-full border rounded-lg p-2.5 text-sm"
                rows={2}
                value={formData.notes}
                onChange={(e) => setFormData((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Any additional notes..." />
              
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={onClose}>{t("cancel", "Cancel")}

              </Button>
              <Button
                type="submit"
                className="bg-red-500 hover:bg-red-600 text-white"
                disabled={loading}>
                
                {loading ?
                <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />{t("saving", "Saving...")}

                </> :

                <>
                    <Plus className="w-4 h-4 mr-2" />{t("addFuelLog", "Add Fuel Log")}

                </>
                }
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>);

};

export default FuelLogsPage;